import type { ComponentFactoryResolver, ComponentRef, Type } from '@angular/core';
import {
  Attribute,
  ChangeDetectorRef,
  Directive,
  EnvironmentInjector,
  Injector,
  ViewContainerRef,
} from '@angular/core';
import type { Data, RouterOutlet, RouterOutletContract, UrlTree } from '@angular/router';
import { ActivatedRoute, ChildrenOutletContexts, Router } from '@angular/router';

import type { Observable } from 'rxjs';
import { from, of } from 'rxjs';

import { microTask } from '@recruitee/utils';

import type { DialogContainerComponent } from '../dialog/dialog-container/dialog-container.component';
import type { DialogRef } from '../dialog/dialog-ref';
import { DialogService } from '../dialogs/dialogs.service';
import { stripChildrenOutlets } from './url-tree-utils';

class OutletInjector implements Injector {
  constructor(
    private route: ActivatedRoute,
    private childContexts: ChildrenOutletContexts,
    private parent: Injector,
  ) {}

  public get(token: any, notFoundValue?: any): any {
    if (token === ActivatedRoute) {
      return this.route;
    }

    if (token === ChildrenOutletContexts) {
      return this.childContexts;
    }

    return this.parent.get(token, notFoundValue);
  }
}

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'dialog-outlet',
  standalone: true,
})
export class DialogOutletDirective implements RouterOutletContract {
  private dialogRef: DialogRef | null;
  private activated: ComponentRef<DialogContainerComponent> | null;
  private initialUrl: UrlTree | null = null;

  constructor(
    private parentContexts: ChildrenOutletContexts,
    private cdRef: ChangeDetectorRef,
    private vcRef: ViewContainerRef,
    private router: Router,
    private dialogService: DialogService,
    private environmentInjector: EnvironmentInjector,
    private _activatedRoute: ActivatedRoute,
    // eslint-disable-next-line @angular-eslint/no-attribute-decorator
    @Attribute('name') private name: string,
  ) {
    if (!name) {
      throw new Error('Dialog Outlet requires name attribute.');
    }

    parentContexts.onChildOutletCreated(this.name, <RouterOutlet>(<unknown>this));
  }

  public ngOnDestroy(): void {
    this.parentContexts.onChildOutletDestroyed(this.name);
  }

  public ngOnInit(): void {
    this.initializeOutletWithName();
  }

  public initializeOutletWithName() {
    this.parentContexts.onChildOutletCreated(this.name, this);
    if (this.activated) {
      return;
    }

    // If the outlet was not instantiated at the time the route got activated we need to populate
    // the outlet when it is initialized (ie inside a NgIf)
    const context = this.parentContexts.getContext(this.name);
    if (context?.route) {
      if (context.attachRef) {
        // `attachRef` is populated when there is an existing component to mount
        this.attach(context.attachRef, context.route);
      } else {
        // otherwise the component defined in the configuration is created
        this.activateWith(context.route, context.injector);
      }
    }
  }

  public get isActivated(): boolean {
    return !!this.activated;
  }

  public get component(): {} {
    if (!this.activated) throw new Error('Outlet is not activated');
    return this.activated.instance;
  }

  public get activatedRoute(): ActivatedRoute {
    if (!this.activated) throw new Error('Outlet is not activated');
    return <ActivatedRoute>this._activatedRoute;
  }

  public get activatedRouteData(): Data {
    if (this._activatedRoute) {
      return this._activatedRoute.snapshot.data;
    }
    return {};
  }

  /**
   * Called when the `RouteReuseStrategy` instructs to detach the subtree
   */
  public detach(): ComponentRef<any> {
    if (!this.activated) throw new Error('Outlet is not activated');
    this.closeDialog();
    this.vcRef.detach();
    const cmp = this.activated;
    this.activated = null;
    return cmp;
  }

  /**
   * Called when the `RouteReuseStrategy` instructs to re-attach a previously detached subtree
   */
  public attach(ref: ComponentRef<any>, activatedRoute: ActivatedRoute) {
    throw new Error('Attach from "RouteReuseStrategy" not implemented in dialog-outlet');
  }

  public deactivate(): void {
    this.closeDialog();
  }

  public activateWith(
    activatedRoute: ActivatedRoute,
    injector?: EnvironmentInjector | ComponentFactoryResolver | null,
  ) {
    this.initialUrl = this.router.getCurrentNavigation()?.initialUrl ?? null;
    this.openDialog(activatedRoute, injector);
  }

  private openDialog(
    activatedRoute: ActivatedRoute,
    injector?: EnvironmentInjector | ComponentFactoryResolver | null,
  ) {
    const { snapshot } = activatedRoute;

    const childContexts = this.parentContexts.getOrCreateContext(this.name).children;

    this.dialogRef = this.dialogService.openCustom(
      <Type<any>>activatedRoute.component,
      {
        ...(snapshot.data.options || {}),
        injector: new OutletInjector(activatedRoute, childContexts, this.vcRef.injector),
        environmentInjector: injector ?? this.environmentInjector,
        closeGuard: () => this.syncLocationOnDialogClose(),
      },
      snapshot.data.dialogData,
      this.vcRef,
    );

    microTask(() => this.dialogRef?.containerInstance.detectChanges());
  }

  private closeDialog() {
    if (this.dialogRef) {
      this.dialogRef.close();
      this.dialogRef = null;
    }
  }

  private syncLocationOnDialogClose(): Observable<boolean> {
    const nav = this.router.getCurrentNavigation();

    if (!nav) {
      const tree = stripChildrenOutlets([this.name], this.router.parseUrl(this.router.url));
      // clear query params created by dialog
      const initialQueryParams = this.initialUrl?.queryParams ?? {};
      const queryParams = Object.entries(tree.queryParams).reduce(
        (acc, [key, value]) => ({
          ...acc,
          ...(key in initialQueryParams ? { [key]: value } : {}),
        }),
        {},
      );
      tree.queryParams = queryParams;
      return from(this.router.navigateByUrl(tree.toString()));
    }

    return of(true);
  }
}
